/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { useServiceContext } from '../../root'
import { useSideNavContext } from './context/useSideNavContext'
import { SideNavIcon } from './SideNavIcon'
import { SideNavItemCustom } from './SideNavItemCustom'
import { usePageContext } from '../../contexts/page-state'
import { useAccessControl } from '../../hooks/useAccessControl'
import { UserRole } from '../../types/access'
import type { User } from '../../types/user'
import MenuFsiSelectorTrigger from './MenuFsiSelectorTrigger'
import StudySiteSelectFolderField from '../staging/StudySiteSelectFolderField'
import useDocUploadDataService from '../../hooks/useDocUploadDataService'
import { Sidebar } from '@mdsol/onex-design'
import { getRole } from '~/services/auth'
import EosSetUpStep1Modal from '~/pages/eos/setup-users-and-workflows/step1/EosSetUpStep1Modal'
import { useModal } from '../../contexts/ModalContext'
import { EosTransferMediaExistingFtpsModal, EosTransferMediaModal } from '~/pages/eos/transfer-media'
import DownloadMediaModal from '~/pages/eos/download-media/DownloadMediaModal'
import PdrPublishModal from '~/pages/eos/pdr/pdr-publish/PdrPublishModal'

export const SideNavItems: React.FC<{ setIsCollapsed?: (isCollapsed: boolean) => void }> = ({ setIsCollapsed }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const sideNavContext = useSideNavContext()
  const serviceContext = useServiceContext()
  const pageContext = usePageContext()
  const { isAllowed, isNotAllowed, hasRole, hasAnyRole } = useAccessControl()
  const { assembleDocUploadData } = useDocUploadDataService()
  const role = getRole()
  const isRcrUser = role === 'RCR_USER'
  const { showModal } = useModal()

  // Section collapse state management (similar to MenuSection)
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(['staging', 'documents', 'tasks', 'changeRequests', 'etmf', 'eos', 'pdr', 'reports', 'settings', 'system'])
  )

  // Get user data and features from service context (matching lego pattern)
  const user: User | null = serviceContext.service.auth.getUser?.() || null
  const featuresInfo = serviceContext.service.init.getFeaturesInfo() || {}

  // Get constants from service context
  const [constants, setConstants] = React.useState<any>({
    system: {
      authTypes: {
        IMEDIDATA: 'IMEDIDATA'
      }
    }
  })

  // Ensure featuresInfo has required structure with fallbacks
  const defaultFeatures = {
    eosFeature: { show: false },
    etmfFeature: { show: false },
    rcrFeature: { show: false },
    allFeaturesData: {
      periodicReviewCustomPropEnabled: false,
      pdrEnabled: false
    },
    qcReview: {
      qcReviewEnabled: false
    }
  }

  // Additional helper state variables (matching legacy MenuContentItems)
  const {
    user: pageUser,
    isCheckmated,
    isStagingUser,
    isStagingUploader,
    isStagingContextSelected,
    policies,
    authTypes,
    featuresInfo: pageFeaturesInfo
  } = pageContext

  // Use page context data where available, fall back to service context
  const safeFeatures = {
    ...defaultFeatures,
    ...(pageFeaturesInfo || featuresInfo)
  }

  // Extract isEnableDocDetailsExpirationDate directly from features (matching legacy MenuContentItems pattern)
  const isEnableDocDetailsExpirationDate = safeFeatures.isEnableDocDetailsExpirationDate || false
  const qcReviewEnabled = safeFeatures.qcReview?.qcReviewEnabled || false

  // Check if a section should be expanded by default based on current route (accordion behavior)
  React.useEffect(() => {
    const currentPath = location.pathname
    let targetSection = ''

    if (currentPath.startsWith('/staging')) targetSection = 'staging'
    else if (currentPath.startsWith('/documents')) targetSection = 'documents'
    else if (currentPath.startsWith('/Tasks')) targetSection = 'tasks'
    else if (currentPath.startsWith('/ChangeRequests')) targetSection = 'changeRequests'
    else if (currentPath.startsWith('/clinical')) targetSection = 'etmf'
    else if (currentPath.startsWith('/eos')) targetSection = 'eos'
    else if (currentPath.startsWith('/pdr')) targetSection = 'pdr'
    else if (currentPath.startsWith('/Reports') || currentPath.startsWith('/reports')) targetSection = 'reports'
    else if (currentPath.startsWith('/settings')) targetSection = 'settings'
    else if (currentPath.startsWith('/system')) targetSection = 'system'

    if (targetSection) {
      const allSections = [
        'staging',
        'documents',
        'tasks',
        'changeRequests',
        'etmf',
        'eos',
        'pdr',
        'reports',
        'settings',
        'system'
      ]

      setCollapsedSections((prev) => {
        // Accordion behavior: collapse all sections except the target one
        const newCollapsed = new Set(allSections.filter((section) => section !== targetSection))

        // Set the expanded section reference
        const sectionRef = sectionRefs.current[targetSection]
        if (sectionRef && sideNavContext.setExpandedMenuSection) {
          sideNavContext.setActiveMenuSection(sectionRef)
          sideNavContext.setExpandedMenuSection(sectionRef)
        }

        return newCollapsed
      })
    }
  }, [location.pathname])

  // Helper to check access permissions (using proper access control)
  const hasAccess = (accessKey: string): boolean => {
    return isAllowed(accessKey)
  }

  // Helper to check if access is denied for specific roles (using proper access control)
  const isAccessDenied = (deniedRoles: string | string[]): boolean => {
    const roles = Array.isArray(deniedRoles) ? deniedRoles : [deniedRoles]
    // Convert string roles to UserRole enum and check if user has any of the denied roles
    const userRoles = roles.filter((role) => Object.values(UserRole).includes(role as UserRole)) as UserRole[]
    return hasAnyRole(userRoles)
  }

  // Create refs for section tracking (similar to MenuSection)
  // Section refs for <a> elements (main nav sections)
  const sectionRefs = React.useRef<Record<string, React.RefObject<HTMLAnchorElement | null>>>(
    {} as Record<string, React.RefObject<HTMLAnchorElement | null>>
  )

  // Initialize section refs
  React.useEffect(() => {
    ;['staging', 'documents', 'tasks', 'changeRequests', 'etmf', 'eos', 'pdr', 'reports', 'settings', 'system'].forEach(
      (sectionId) => {
        if (!sectionRefs.current[sectionId]) {
          sectionRefs.current[sectionId] =
            React.createRef<HTMLAnchorElement>() as React.RefObject<HTMLAnchorElement | null>
        }
      }
    )
  }, [])

  // Section collapse management functions (accordion behavior - only one section expanded at a time)
  const toggleSectionCollapse = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const allSections = [
        'staging',
        'documents',
        'tasks',
        'changeRequests',
        'etmf',
        'eos',
        'pdr',
        'reports',
        'settings',
        'system'
      ]

      if (sideNavContext.isMenuCollapsed) {
        // If main menu is collapsed, expand it first and open the section
        sideNavContext.expandMenu()
        // Collapse all sections except the target one (accordion behavior)
        const newCollapsed = new Set(allSections.filter((section) => section !== sectionId))

        // Set the expanded section reference
        const sectionRef = sectionRefs.current[sectionId]
        if (sectionRef && sideNavContext.setExpandedMenuSection) {
          sideNavContext.setExpandedMenuSection(sectionRef)
        }

        return newCollapsed
      } else {
        // Check if this section is currently collapsed (we want to expand it)
        const isCurrentlyCollapsed = prev.has(sectionId)

        if (isCurrentlyCollapsed) {
          // Expand this section and collapse all others (accordion behavior)
          const newCollapsed = new Set(allSections.filter((section) => section !== sectionId))

          // Set the expanded section reference
          const sectionRef = sectionRefs.current[sectionId]
          if (sectionRef && sideNavContext.setExpandedMenuSection) {
            sideNavContext.setExpandedMenuSection(sectionRef)
          }

          return newCollapsed
        } else {
          // This section is currently expanded, collapse it (and collapse all others too)
          const newCollapsed = new Set(allSections)

          // Clear the expanded section reference
          if (sideNavContext.setExpandedMenuSection) {
            sideNavContext.setExpandedMenuSection(null)
          }

          return newCollapsed
        }
      }
    })
  }

  const isSectionCollapsed = (sectionId: string): boolean => {
    return collapsedSections.has(sectionId)
  }

  return (
    <>
      <Sidebar
        isCollapsible
        isExpanded
        onToggle={() => {
          sideNavContext.collapseMenu()
          //@ts-expect-error To match legacy behaviour
          setIsCollapsed && setIsCollapsed((prev: any) => !prev)
        }}
      >
        <div className="side-nav side-nav-section custom-changes">
          <ul className="side-nav-list">
            <SideNavItemCustom
              id="overview-nav-item"
              className=""
              title={sideNavContext.isMenuCollapsed ? t('SUMMARY', 'Summary') : ''}
              to={
                isStagingUser || isRcrUser
                  ? '/staging/overview'
                  : safeFeatures.etmfFeature?.show && hasAccess('clinicalAccess')
                    ? '/TrialOverview/status'
                    : '/overview'
              }
            >
              <span className="fs14">{t('SUMMARY', 'Summary')}</span>
            </SideNavItemCustom>
            {/* Notifications Navigation Item (matching MenuContent.jsx pattern) */}
            <SideNavItemCustom id="notifications-nav-item" className="" to="/notifications">
              <span className="fs14">{t('NOTIFICATIONS', 'Notifications')}</span>
            </SideNavItemCustom>
            {hasAccess('documentsAccess') && !isStagingUploader && (
              <SideNavItemCustom to="/documents/favorites" name="FAVORITES">
                <span className="fs14">{t('FAVORITES', 'Favorites')}</span>
              </SideNavItemCustom>
            )}
            {isStagingUploader && (
              <li className={`side-nav-item${isStagingUser && !isStagingContextSelected ? ' disabled' : ''}`}>
                <SideNavItemCustom to="/common-favorites" name="FAVORITES">
                  <span className="fs14">{t('FAVORITES', 'Favorites')}</span>
                </SideNavItemCustom>
              </li>
            )}
            {/* Staging Section - Only show for staging users (matching MenuContentItems) */}
            {hasAccess('stagingUsers') && (
              <li className={`side-nav-item${isStagingUser && !isStagingContextSelected ? ' disabled' : ''}`}>
                <a
                  ref={sectionRefs.current['staging']}
                  onClick={() => toggleSectionCollapse('staging')}
                  className={
                    sideNavContext.activeMenuSection === sectionRefs.current['staging'] ||
                    (sectionRefs.current['staging']?.current &&
                      sideNavContext.activeMenuSection?.current &&
                      sectionRefs.current['staging']?.current.contains(sideNavContext.activeMenuSection.current))
                      ? ' active'
                      : ''
                  }
                  title={sideNavContext.isMenuCollapsed ? t('RIGHT_TOP_NAV_BROWSER', 'Browser') : ''}
                  data-test-id="main-nav-staging"
                >
                  <span className="fs14">{t('RIGHT_TOP_NAV_BROWSER', 'Browser')}</span>
                  {!(isStagingUser && !isStagingContextSelected) &&
                    // <span className="side-nav-arrow">{isSectionCollapsed('staging') ? '' : '▲'}</span>
                    (isSectionCollapsed('staging') ? (
                      <i className="pull-right bold fa fa-angle-right" />
                    ) : (
                      <i className="pull-right bold fa fa-angle-down" />
                    ))}
                </a>
                {!isSectionCollapsed('staging') && isStagingContextSelected && (
                  <ul className="side-nav-sublist">
                    <>
                      {/* Study Site Select Folder Field for staging uploaders (matching MenuContentItems) */}
                      {isStagingUploader && !isSectionCollapsed('staging') && <StudySiteSelectFolderField />}

                      <SideNavItemCustom to="/staging/stagingarea" name="STAGING_AREA_SECTION">
                        <span className="fs14">{t('STAGING_AREA_SECTION', 'Staging Area')}</span>
                      </SideNavItemCustom>

                      <SideNavItemCustom to="/staging/MissingDocuments" name="STAGING_AREA_SECTION_MISSING_DOCS">
                        <span className="fs14">{t('STAGING_AREA_SECTION_MISSING_DOCS', 'Missing Documents')}</span>
                      </SideNavItemCustom>

                      <SideNavItemCustom
                        to="#"
                        name="STAGING_AREA_SECTION_UPLOAD_DOCS"
                        onClick={() => {
                          assembleDocUploadData()
                        }}
                      >
                        <span className="fs14">{t('STAGING_AREA_SECTION_UPLOAD_DOCS', 'Upload Documents')}</span>
                      </SideNavItemCustom>

                      <SideNavItemCustom to="/staging/MyDocuments" name="STAGING_AREA_SECTION_USER_DOCS">
                        <span className="fs14">{t('STAGING_AREA_SECTION_USER_DOCS', 'User Documents')}</span>
                      </SideNavItemCustom>

                      {serviceContext.service.staging &&
                        serviceContext.service.staging.getLevel() === serviceContext.service.staging.LEVEL.site && (
                          <SideNavItemCustom to="/staging/Documents" name="STAGING_AREA_SECTION_SITE_DOCS">
                            <span className="fs14">{t('STAGING_AREA_SECTION_SITE_DOCS', 'Site Documents')}</span>
                          </SideNavItemCustom>
                        )}
                      {serviceContext.service.staging &&
                        serviceContext.service.staging.getLevel() ===
                          serviceContext.service.staging.LEVEL.environment && (
                          <SideNavItemCustom to="/staging/StudyDocuments" name="STAGING_AREA_SECTION_STUDY_DOCS">
                            <span className="fs14">{t('STAGING_AREA_SECTION_STUDY_DOCS', 'Study Documents')}</span>
                          </SideNavItemCustom>
                        )}

                      <SideNavItemCustom to="/staging/property-search" name="STAGING_AREA_SECTION_PROPERTY_SEARCH">
                        <span className="fs14">{t('STAGING_AREA_SECTION_PROPERTY_SEARCH', 'Property Search')}</span>
                      </SideNavItemCustom>
                    </>
                  </ul>
                )}
              </li>
            )}

            {/* Documents Section - Hide for staging uploaders (matching MenuContentItems) */}

            {hasAccess('documentsAccess') && !isStagingUploader && (
              <li className="side-nav-item">
                <a
                  ref={sectionRefs.current['documents']}
                  onClick={() => toggleSectionCollapse('documents')}
                  className={
                    sideNavContext.activeMenuSection === sectionRefs.current['documents'] ||
                    (sectionRefs.current['documents']?.current &&
                      sideNavContext.activeMenuSection?.current &&
                      sectionRefs.current['documents']?.current.contains(sideNavContext.activeMenuSection.current))
                      ? ' active'
                      : ''
                  }
                  title={sideNavContext.isMenuCollapsed ? t('RIGHT_TOP_NAV_BROWSER', 'Documents') : ''}
                  data-test-id="main-nav-documents"
                >
                  {t('RIGHT_TOP_NAV_BROWSER', 'Documents')}
                  {/* <span className="side-nav-arrow">{isSectionCollapsed('documents') ? '▼' : '▲'}</span> */}
                  {isSectionCollapsed('documents') ? (
                    <i className="pull-right bold fa fa-angle-right" />
                  ) : (
                    <i className="pull-right bold fa fa-angle-down" />
                  )}
                </a>
                {!isSectionCollapsed('documents') && (
                  <ul className="side-nav-sublist">
                    {hasAccess('documentsAccessRegulatedContent') && (
                      <SideNavItemCustom
                        to={`/browser/${serviceContext.service.auth.getBrowser?.('REGULATED_CONTENT')?.id || 1}?page=1&type=REGULATED_CONTENT`}
                        name="ALL_FOLDERS_DOCUMENTS"
                        title={t('ALL_FOLDERS_DOCUMENTS', 'All Documents')}
                      >
                        <span className="fs14">{t('ALL_FOLDERS_DOCUMENTS', 'All Documents')}</span>
                        {/* <div className="d-flex justify-content-between align-items-center">
                          <span className="fs14">{t('ALL_FOLDERS_DOCUMENTS', 'All Documents')}</span>
                          <MenuFsiSelectorTrigger />
                        </div> */}
                      </SideNavItemCustom>
                    )}
                    {hasAccess('documentsAccessRecent') && (
                      <SideNavItemCustom to="/documents/recent" name="RECENT_DOCUMENTS">
                        <span className="fs14">{t('RECENT_DOCUMENTS', 'Recent Documents')}</span>
                      </SideNavItemCustom>
                    )}
                    {safeFeatures.allFeaturesData?.periodicReviewCustomPropEnabled &&
                      hasAccess('documentsAccessPeriodicReview') && (
                        <SideNavItemCustom to="/documents/periodic-review" name="PERIODIC_REVIEW">
                          <span className="fs14">{t('PERIODIC_REVIEW', 'Periodic Review')}</span>
                        </SideNavItemCustom>
                      )}
                    {hasAccess('documentsAccessPropertiesSearch') && (
                      <SideNavItemCustom to="/documents/property-search" name="BROWSER_VIEW_PROPERTY_FILTER">
                        <span className="fs14">{t('BROWSER_VIEW_PROPERTY_FILTER', 'Property Search')}</span>
                      </SideNavItemCustom>
                    )}
                    {!isAccessDenied('INSPECTOR') && (
                      <SideNavItemCustom to="/documents/system-documents" name="SYSTEM_DOCUMENTS">
                        <span className="fs14">{t('SYSTEM_DOCUMENTS', 'System Documents')}</span>
                      </SideNavItemCustom>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Tasks Section */}
            {hasAccess('tasksAccess') && (
              <li className="side-nav-item">
                <a
                  ref={sectionRefs.current['tasks']}
                  onClick={() => toggleSectionCollapse('tasks')}
                  className={
                    sideNavContext.activeMenuSection === sectionRefs.current['tasks'] ||
                    (sectionRefs.current['tasks']?.current &&
                      sideNavContext.activeMenuSection?.current &&
                      sectionRefs.current['tasks']?.current.contains(sideNavContext.activeMenuSection.current))
                      ? ' active'
                      : ''
                  }
                  title={sideNavContext.isMenuCollapsed ? t('RIGHT_TOP_NAV_TASKS', 'Tasks') : ''}
                  data-test-id="main-nav-tasks"
                >
                  {t('RIGHT_TOP_NAV_TASKS', 'Tasks')}
                  {/* <span className="side-nav-arrow">{isSectionCollapsed('tasks') ? '▼' : '▲'}</span> */}
                  {isSectionCollapsed('tasks') ? (
                    <i className="pull-right bold  fa fa-angle-right" />
                  ) : (
                    <i className="pull-right bold  fa fa-angle-down" />
                  )}
                </a>
                {!isSectionCollapsed('tasks') && (
                  <ul className="side-nav-sublist">
                    <SideNavItemCustom to="/Tasks/me" name="TASKS_FILTERS_ME">
                      <span className="fs14">{t('TASKS_FILTERS_ME', 'My Tasks')}</span>
                    </SideNavItemCustom>
                    {hasAccess('tasksAccessAllOpen') && (
                      <SideNavItemCustom to="/Tasks/all-opened" name="TASKS_FILTERS_ALL_OPENED">
                        <span className="fs14">{t('TASKS_FILTERS_ALL_OPENED', 'All Open Tasks')}</span>
                      </SideNavItemCustom>
                    )}
                    <SideNavItemCustom to="/Tasks/all" name="TASKS_FILTERS_ALL">
                      <span className="fs14">{t('TASKS_FILTERS_ALL', 'All Tasks')}</span>
                    </SideNavItemCustom>
                    <SideNavItemCustom to="/Tasks/completed" name="TASKS_FILTERS_COMPLETED">
                      <span className="fs14">{t('TASKS_FILTERS_COMPLETED', 'Completed Tasks')}</span>
                    </SideNavItemCustom>
                  </ul>
                )}
              </li>
            )}

            {/* Workflows Section */}
            {hasAccess('workflowsAccess') && (
              <>
                <li className="side-nav-item">
                  <a
                    ref={sectionRefs.current['changeRequests']}
                    onClick={() => toggleSectionCollapse('changeRequests')}
                    className={
                      sideNavContext.activeMenuSection === sectionRefs.current['changeRequests'] ||
                      (sectionRefs.current['changeRequests']?.current &&
                        sideNavContext.activeMenuSection?.current &&
                        sectionRefs.current['changeRequests']?.current.contains(
                          sideNavContext.activeMenuSection.current
                        ))
                        ? ' active'
                        : ''
                    }
                    title={sideNavContext.isMenuCollapsed ? t('RIGHT_TOP_NAV_ACTIVE_WORKFLOWS', 'Workflows') : ''}
                    data-test-id="main-nav-workflows"
                  >
                    {t('RIGHT_TOP_NAV_ACTIVE_WORKFLOWS', 'Workflows')}
                    {/* <span className="side-nav-arrow">{isSectionCollapsed('changeRequests') ? '▼' : '▲'}</span> */}
                    {isSectionCollapsed('changeRequests') ? (
                      <i className="pull-right bold  fa fa-angle-right" />
                    ) : (
                      <i className="pull-right bold  fa fa-angle-down" />
                    )}
                  </a>
                  {!isSectionCollapsed('changeRequests') && (
                    <ul className="side-nav-sublist">
                      <SideNavItemCustom to="/ChangeRequests/active" name="WORKFLOWS_FILTERS_ACTIVE">
                        <span className="fs14">{t('WORKFLOWS_FILTERS_ACTIVE', 'Active Workflows')}</span>
                      </SideNavItemCustom>
                      <SideNavItemCustom to="/ChangeRequests/finished" name="WORKFLOWS_FILTERS_ARCHIVED">
                        <span className="fs14">{t('WORKFLOWS_FILTERS_ARCHIVED', 'Archived Workflows')}</span>
                      </SideNavItemCustom>
                      <SideNavItemCustom to="/ChangeRequests/all" name="WORKFLOWS_FILTERS_ALL">
                        <span className="fs14">{t('WORKFLOWS_FILTERS_ALL', 'All Workflows')}</span>
                      </SideNavItemCustom>
                      {safeFeatures.eosFeature?.show && user?.role === 'ADMIN' && (
                        <SideNavItemCustom to="/ChangeRequests/SubmittedWorkflowInstructions" name="WORKFLOW_REQUESTS">
                          <span className="fs14">{t('WORKFLOW_REQUESTS', 'Workflow Requests')}</span>
                        </SideNavItemCustom>
                      )}
                    </ul>
                  )}
                </li>
              </>
            )}

            {/* ETMF Section - Enhanced feature and access control (matching MenuContentItems) */}
            {safeFeatures.etmfFeature?.show && hasAccess('clinicalAccess') && !isAccessDenied(['ETMF_SPONSOR']) && (
              <li className="side-nav-item">
                <a
                  ref={sectionRefs.current['etmf']}
                  onClick={() => toggleSectionCollapse('etmf')}
                  className={
                    sideNavContext.activeMenuSection === sectionRefs.current['etmf'] ||
                    (sectionRefs.current['etmf']?.current &&
                      sideNavContext.activeMenuSection?.current &&
                      sectionRefs.current['etmf']?.current.contains(sideNavContext.activeMenuSection.current))
                      ? ' active'
                      : ''
                  }
                  title={sideNavContext.isMenuCollapsed ? t('RIGHT_TOP_NAV_ETMF', 'eTMF') : ''}
                  data-test-id="main-nav-etmf"
                >
                  {t('RIGHT_TOP_NAV_ETMF_CONFIGURATION', 'eTMF Configuration')}
                  {/* <span className="side-nav-arrow">{isSectionCollapsed('etmf') ? '▼' : '▲'}</span> */}
                  {isSectionCollapsed('etmf') ? (
                    <i className="pull-right bold fa fa-angle-right" />
                  ) : (
                    <i className="pull-right bold fa fa-angle-down" />
                  )}
                </a>
                {!isSectionCollapsed('etmf') && (
                  <ul className="side-nav-sublist">
                    {/* <SideNavItemCustom to="/TrialOverview/status" name="ETMF_TRIAL_OVERVIEW">
                      <span className="fs14">{t('ETMF_TRIAL_OVERVIEW', 'Trial Overview')}</span>
                    </SideNavItemCustom> */}
                    {!isAccessDenied(['ETMF_SPECIALIST', 'ETMF_SPONSOR']) && (
                      <>
                        <SideNavItemCustom to="/clinical/products" name="EMTF_MD_PRODUCTS">
                          <span className="fs14">{t('EMTF_MD_PRODUCTS', 'Products')}</span>
                        </SideNavItemCustom>
                        <SideNavItemCustom to="/clinical/studies" name="EMTF_MD_TRIALS">
                          <span className="fs14">{t('EMTF_MD_TRIALS', 'Trials')}</span>
                        </SideNavItemCustom>
                        <SideNavItemCustom to="/clinical/environments" name="EMTF_MD_ENVIRONMENTS">
                          <span className="fs14">{t('EMTF_MD_ENVIRONMENTS', 'Environments')}</span>
                        </SideNavItemCustom>
                        <SideNavItemCustom to="/clinical/countries" name="EMTF_MD_COUNTRIES">
                          <span className="fs14">{t('EMTF_MD_COUNTRIES', 'Countries')}</span>
                        </SideNavItemCustom>
                        <SideNavItemCustom to="/clinical/sites" name="EMTF_MD_SITES">
                          <span className="fs14">{t('EMTF_MD_SITES', 'Sites')}</span>
                        </SideNavItemCustom>
                        <SideNavItemCustom to="/clinical/file-plans" name="EMTF_FILEPLANS">
                          <span className="fs14">{t('EMTF_FILEPLANS', 'File Plans')}</span>
                        </SideNavItemCustom>
                      </>
                    )}
                    {!isAccessDenied('ETMF_SPONSOR') && (
                      <SideNavItemCustom to="/clinical/packages" name="EMTF_PACKAGES_TITLE">
                        <span className="fs14">{t('EMTF_PACKAGES_TITLE', 'Packages')}</span>
                      </SideNavItemCustom>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* EOS Section */}
            {authTypes.allowedAuthTypes?.indexOf(constants.system.authTypes.IMEDIDATA) > -1 &&
              safeFeatures.eosFeature?.show &&
              hasAccess('eosMediaAccess') && (
                <li className="side-nav-item">
                  <a
                    ref={sectionRefs.current['eos']}
                    onClick={() => toggleSectionCollapse('eos')}
                    className={
                      sideNavContext.activeMenuSection === sectionRefs.current['eos'] ||
                      (sectionRefs.current['eos']?.current &&
                        sideNavContext.activeMenuSection?.current &&
                        sectionRefs.current['eos']?.current.contains(sideNavContext.activeMenuSection.current))
                        ? ' active'
                        : ''
                    }
                    title={sideNavContext.isMenuCollapsed ? t('RIGHT_TOP_NAV_EOS_MEDIA', 'EOS Media') : ''}
                    data-test-id="main-nav-eos"
                  >
                    {t('RIGHT_TOP_NAV_EOS_MEDIA', 'EOS Media')}
                    {/* <span className="side-nav-arrow">{isSectionCollapsed('eos') ? '▼' : '▲'}</span> */}
                    {isSectionCollapsed('eos') ? (
                      <i className="pull-right bold fa fa-angle-right" />
                    ) : (
                      <i className="pull-right bold fa fa-angle-down" />
                    )}
                  </a>
                  {!isSectionCollapsed('eos') && (
                    <ul className="side-nav-sublist">
                      {hasAccess('eosMediaAccessOverview') && (
                        <SideNavItemCustom to="/EosStudyOverview" name="ETMF_TRIAL_OVERVIEW">
                          <span className="fs14">{t('ETMF_TRIAL_OVERVIEW', 'Trial Overview')}</span>
                        </SideNavItemCustom>
                      )}
                      {policies.containsLegacyEOSStudy && hasAccess('eosMediaAccessOverview') && (
                        <SideNavItemCustom to="/EosStudyOverviewLegacy" name="ETMF_TRIAL_OVERVIEW_LEGACY">
                          <span className="fs14">{t('ETMF_TRIAL_OVERVIEW_LEGACY', 'Trial Overview (Legacy)')}</span>
                        </SideNavItemCustom>
                      )}
                      {hasAccess('eosMediaAccessTransferMedia') && (
                        <SideNavItemCustom
                          to="#"
                          name="EOS_TRANSFER_MEDIA"
                          onClick={() => {
                            showModal(<EosTransferMediaModal />)
                          }}
                        >
                          <span className="fs14">{t('EOS_TRANSFER_MEDIA', 'Transfer Media')}</span>
                        </SideNavItemCustom>
                      )}
                      {((user?.role === 'POWER_USER' &&
                        user?.eosSponsorPowerUserPlus &&
                        hasAccess('eosMediaAccessSetUpPowerUser')) ||
                        (user?.role !== 'POWER_USER' && hasAccess('eosMediaAccessSetUp'))) && (
                        <SideNavItemCustom
                          to="#"
                          name={
                            user?.role === 'POWER_USER' && user?.eosSponsorPowerUserPlus
                              ? 'EOS_SPONSOR_POWER_USER_PLUS_SETUP_USERS_AND_WORKFLOWS_REQUEST'
                              : 'EOS_SETUP_USERS_WF'
                          }
                          onClick={() => {
                            showModal(<EosSetUpStep1Modal params={{ config: {}, options: {} }} />)
                          }}
                        >
                          <span className="fs14">
                            {t(
                              user?.role === 'POWER_USER' && user?.eosSponsorPowerUserPlus
                                ? 'EOS_SPONSOR_POWER_USER_PLUS_SETUP_USERS_AND_WORKFLOWS_REQUEST'
                                : 'EOS_SETUP_USERS_WF',
                              'Setup Users & Workflows'
                            )}
                          </span>
                        </SideNavItemCustom>
                      )}
                      {hasAccess('eosMediaAccessDownloadMedia') && (
                        <SideNavItemCustom
                          to="#"
                          name="EOS_DOWNLOAD_MEDIA"
                          onClick={() => {
                            showModal(<DownloadMediaModal />)
                          }}
                        >
                          <span className="fs14">{t('EOS_DOWNLOAD_MEDIA', 'Download Media')}</span>
                        </SideNavItemCustom>
                      )}
                    </ul>
                  )}
                </li>
              )}

            {/* PDR Section */}
            {authTypes.allowedAuthTypes?.includes(constants.system.authTypes.IMEDIDATA) &&
              safeFeatures.allFeaturesData?.pdrEnabled &&
              hasAccess('eosMediaAccessSetUp') && (
                <li className="side-nav-item">
                  <a
                    ref={sectionRefs.current['pdr']}
                    onClick={() => toggleSectionCollapse('pdr')}
                    className={
                      sideNavContext.activeMenuSection === sectionRefs.current['pdr'] ||
                      (sectionRefs.current['pdr']?.current &&
                        sideNavContext.activeMenuSection?.current &&
                        sectionRefs.current['pdr']?.current.contains(sideNavContext.activeMenuSection.current))
                        ? ' active'
                        : ''
                    }
                    title={sideNavContext.isMenuCollapsed ? t('RIGHT_TOP_NAV_EOS_PDR', 'PDR') : ''}
                    data-test-id="main-nav-pdr"
                  >
                    {t('RIGHT_TOP_NAV_EOS_PDR', 'PDR')}
                    {/* <span className="side-nav-arrow">{isSectionCollapsed('pdr') ? '▼' : '▲'}</span> */}
                    {isSectionCollapsed('pdr') ? (
                      <i className="pull-right bold fa fa-angle-right" />
                    ) : (
                      <i className="pull-right bold fa fa-angle-down" />
                    )}
                  </a>
                  {!isSectionCollapsed('pdr') && (
                    <ul className="side-nav-sublist">
                      <SideNavItemCustom
                        to="#"
                        name="PDR_STUDIES"
                        onClick={() => showModal(<EosTransferMediaExistingFtpsModal isPdrFlow />)}
                      >
                        <span className="fs14">{t('PDR_STUDIES', 'PDR Studies')}</span>
                      </SideNavItemCustom>
                      <SideNavItemCustom
                        to="#"
                        name="PDR_PUBLISH"
                        onClick={() => {
                          showModal(<PdrPublishModal />)
                        }}
                      >
                        <span className="fs14">{t('PDR_PUBLISH', 'PDR Publish')}</span>
                      </SideNavItemCustom>
                    </ul>
                  )}
                </li>
              )}

            {/* Reports Section */}
            {user?.canCreateReports && hasAccess('reportsAccess') && (
              <li className="side-nav-item">
                <a
                  ref={sectionRefs.current['reports']}
                  onClick={() => toggleSectionCollapse('reports')}
                  className={
                    sideNavContext.activeMenuSection === sectionRefs.current['reports'] ||
                    (sectionRefs.current['reports']?.current &&
                      sideNavContext.activeMenuSection?.current &&
                      sectionRefs.current['reports']?.current.contains(sideNavContext.activeMenuSection.current))
                      ? ' active'
                      : ''
                  }
                  title={sideNavContext.isMenuCollapsed ? t('RIGHT_TOP_NAV_REPORTS', 'Reports') : ''}
                  data-test-id="main-nav-reports"
                >
                  {t('RIGHT_TOP_NAV_REPORTS', 'Reports')}
                  {/* <span className="side-nav-arrow">{isSectionCollapsed('reports') ? '▼' : '▲'}</span> */}
                  {isSectionCollapsed('reports') ? (
                    <i className="pull-right bold fa fa-angle-right" />
                  ) : (
                    <i className="pull-right bold fa fa-angle-down" />
                  )}
                </a>
                {!isSectionCollapsed('reports') && (
                  <ul className="side-nav-sublist">
                    {safeFeatures.eosFeature?.show &&
                      authTypes.allowedAuthTypes?.includes(constants.system.authTypes.IMEDIDATA) &&
                      hasAccess('reportsEos') && (
                        <SideNavItemCustom to="/reports/EosWorkflows" name="EOS_WORKFLOW_REPORT">
                          <span className="fs14">{t('EOS_WORKFLOW_REPORT', 'EOS Workflow Report')}</span>
                        </SideNavItemCustom>
                      )}
                    {safeFeatures.eosFeature?.show &&
                      ((user?.role === 'POWER_USER' &&
                        user?.eosSponsorPowerUserPlus &&
                        hasAccess('eosMediaAccessSetUpPowerUser')) ||
                        (user?.role !== 'POWER_USER' && hasAccess('eosMediaAccessSetUp'))) && (
                        <SideNavItemCustom to="/reports/EosWorkflowsRequest" name="EOS_WORKFLOWS_REQUESTS">
                          <span className="fs14">{t('EOS_WORKFLOWS_REQUESTS', 'EOS Workflows Requests')}</span>
                        </SideNavItemCustom>
                      )}
                    {safeFeatures.eosFeature?.show &&
                      policies.containsLegacyEOSStudy &&
                      authTypes.allowedAuthTypes?.includes(constants.system.authTypes.IMEDIDATA) &&
                      hasAccess('reportsAccessEosDownloadActivity') && (
                        <SideNavItemCustom to="/reports/EosDownloadActivity" name="EOS_DOWNLOAD_ACTIVITY_REPORT">
                          <span className="fs14">
                            {t('EOS_DOWNLOAD_ACTIVITY_REPORT', 'EOS Download Activity Report')}
                          </span>
                        </SideNavItemCustom>
                      )}
                    {safeFeatures.eosFeature?.show &&
                      policies.containsLegacyEOSStudy &&
                      authTypes.allowedAuthTypes?.includes(constants.system.authTypes.IMEDIDATA) &&
                      hasAccess('reportsAccessEosAcknowledgementActivity') && (
                        <SideNavItemCustom
                          to="/reports/EosAcknowledgementActivity"
                          name="EOS_ACKNOWLEDGEMENT_ACTIVITY_REPORT"
                        >
                          <span className="fs14">
                            {t('EOS_ACKNOWLEDGEMENT_ACTIVITY_REPORT', 'EOS Acknowledgement Activity Report')}
                          </span>
                        </SideNavItemCustom>
                      )}
                    {user?.canCreateActivityReports && hasAccess('reportsAccessActivity') && (
                      <SideNavItemCustom to="/Reports/Activity" name="ACTIVITY_TITLE">
                        <span className="fs14">{t('ACTIVITY_TITLE', 'Activity Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {safeFeatures.etmfFeature?.show && hasAccess('reportsAccessClinicalActivity') && (
                      <SideNavItemCustom to="/Reports/ClinicalActivity" name="CLINICAL_ACTIVITY_TITLE">
                        <span className="fs14">{t('CLINICAL_ACTIVITY_TITLE', 'Clinical Activity Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {safeFeatures.etmfFeature?.show && hasAccess('reportsAccessEtmfCompletion') && (
                      <SideNavItemCustom to="/Reports/eTMFCompletion" name="ETMF_COMPLETION_REPORT_TITLE">
                        <span className="fs14">{t('ETMF_COMPLETION_REPORT_TITLE', 'ETMF Completion Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {safeFeatures.etmfFeature?.show && hasAccess('reportsAccessEtmfTimeliness') && (
                      <SideNavItemCustom to="/Reports/eTMFTimeliness" name="ETMF_TIMELINESS_REPORT_TITLE">
                        <span className="fs14">{t('ETMF_TIMELINESS_REPORT_TITLE', 'ETMF Timeliness Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {safeFeatures.etmfFeature?.show && hasAccess('reportsAccessEtmfFiledDocs') && (
                      <SideNavItemCustom to="/Reports/eTMFFiledDocs" name="ETMF_FILED_DOCS_REPORT_TITLE">
                        <span className="fs14">{t('ETMF_FILED_DOCS_REPORT_TITLE', 'ETMF Filed Documents Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {safeFeatures.etmfFeature?.show && hasAccess('reportsAccessEtmfDocDetail') && (
                      <SideNavItemCustom to="/Reports/eTMFDocDetail" name="ETMF_DOC_DETAIL_REPORT_TITLE">
                        <span className="fs14">{t('ETMF_DOC_DETAIL_REPORT_TITLE', 'ETMF Document Detail Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {safeFeatures.etmfFeature?.show && hasAccess('reportsAccessEtmfContributorDocStatus') && (
                      <SideNavItemCustom
                        to="/Reports/eTMFContributorDocStatus"
                        name="ETMF_CONTRIBUTOR_DOC_STATUS_REPORT_TITLE"
                      >
                        <span className="fs14">
                          {t('ETMF_CONTRIBUTOR_DOC_STATUS_REPORT_TITLE', 'ETMF Contributor Doc Status Report')}
                        </span>
                      </SideNavItemCustom>
                    )}
                    {hasAccess('reportsAccessDuplicateDocuments') && (
                      <SideNavItemCustom to="/Reports/DuplicateDocuments" name="DUPLICATE_DOCUMENTS_REPORT">
                        <span className="fs14">{t('DUPLICATE_DOCUMENTS_REPORT', 'Duplicate Documents Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {safeFeatures.qcReview?.qcReviewEnabled && hasAccess('reportsAccessQCReview') && (
                      <SideNavItemCustom to="/Reports/QcReview" name="QC_REVIEW_REPORT_TITLE">
                        <span className="fs14">{t('QC_REVIEW_REPORT_TITLE', 'QC Review Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {hasAccess('reportsAccessAccessRights') && (
                      <SideNavItemCustom to="/Reports/AccessRights" name="ACLS_REPORTS_TITLE">
                        <span className="fs14">{t('ACLS_REPORTS_TITLE', 'Access Rights Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {user?.canCreateActivityReports && hasAccess('reportsAccessReadAcknowledge') && (
                      <SideNavItemCustom to="/Reports/Training" name="TRAINING_TITLE">
                        <span className="fs14">{t('TRAINING_TITLE', 'Training Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {hasAccess('reportsAccessUsersUsage') && (
                      <SideNavItemCustom to="/Reports/Users" name="USERS_REPORTS_TITLE">
                        <span className="fs14">{t('USERS_REPORTS_TITLE', 'Users and Usage Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {hasAccess('reportsAccessGroupMembership') && (
                      <SideNavItemCustom to="/Reports/GroupMembership" name="GROUP_MEMBERSHIP_REPORTS_TITLE">
                        <span className="fs14">{t('GROUP_MEMBERSHIP_REPORTS_TITLE', 'Group Membership Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {user?.canCreateActivityReports && hasAccess('reportsAccessChangeRequests') && (
                      <SideNavItemCustom to="/Reports/ChangeRequests" name="CHANGE_REQUESTS_REPORTS_TITLE">
                        <span className="fs14">{t('CHANGE_REQUESTS_REPORTS_TITLE', 'Change Requests Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {hasAccess('reportsAccessChangeRequestTypes') && (
                      <SideNavItemCustom to="/Reports/ChangeRequestsTypes" name="CR_TYPES_REPORTS_TITLE">
                        <span className="fs14">{t('CR_TYPES_REPORTS_TITLE', 'CR Types Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {hasAccess('reportsAccessPropertiesSearch') && (
                      <SideNavItemCustom to="/Reports/PropertySearch" name="REPORTS_PROPERTIES_SEARCH_TITLE">
                        <span className="fs14">{t('REPORTS_PROPERTIES_SEARCH_TITLE', 'Properties Search Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {hasAccess('reportsAccessPropertiesUsage') && (
                      <SideNavItemCustom to="/Reports/PropertiesUsage" name="PROPERTIES_USAGE_REPORTS_TITLE">
                        <span className="fs14">{t('PROPERTIES_USAGE_REPORTS_TITLE', 'Properties Usage Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {user?.canCreateActivityReports && hasAccess('reportsAccessTasks') && (
                      <SideNavItemCustom to="/Reports/Tasks" name="TASKS_REPORTS_TITLE">
                        <span className="fs14">{t('TASKS_REPORTS_TITLE', 'Tasks Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {user?.canCreateActivityReports && hasAccess('reportsAccessRejectionReasons') && (
                      <SideNavItemCustom to="/Reports/Rejections" name="REJECTION_REASONS_REPORTS_TITLE">
                        <span className="fs14">{t('REJECTION_REASONS_REPORTS_TITLE', 'Rejection Reasons Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {isEnableDocDetailsExpirationDate && hasAccess('reportsAccessDocumentExpiration') && (
                      <SideNavItemCustom to="/Reports/DocExpiration" name="DOC_EXPIRATION_REPORTS_TITLE">
                        <span className="fs14">{t('DOC_EXPIRATION_REPORTS_TITLE', 'Document Expiration Report')}</span>
                      </SideNavItemCustom>
                    )}
                    {hasAccess('reportsAccessCustom') && (
                      <SideNavItemCustom to="/Reports/Custom" name="CUSTOM_REPORTS_TITLE">
                        <span className="fs14">{t('CUSTOM_REPORTS_TITLE', 'Custom Reports')}</span>
                      </SideNavItemCustom>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* Settings Section - Enhanced access control (matching MenuContentItems) */}
            {/* Note: Legacy only uses accessDenied="EOS_SITE_USER", no access requirement on section wrapper */}
            {!isAccessDenied('EOS_SITE_USER') && (
              <li className="side-nav-item">
                <a
                  ref={sectionRefs.current['settings']}
                  onClick={() => toggleSectionCollapse('settings')}
                  className={
                    sideNavContext.activeMenuSection === sectionRefs.current['settings'] ||
                    (sectionRefs.current['settings']?.current &&
                      sideNavContext.activeMenuSection?.current &&
                      sectionRefs.current['settings']?.current.contains(sideNavContext.activeMenuSection.current))
                      ? ' active'
                      : ''
                  }
                  title={sideNavContext.isMenuCollapsed ? t('RIGHT_TOP_NAV_SETTINGS', 'Settings') : ''}
                  data-test-id="main-nav-settings"
                >
                  {t('RIGHT_TOP_NAV_SETTINGS', 'Settings')}
                  {/* <span className="side-nav-arrow">{isSectionCollapsed('settings') ? '▼' : '▲'}</span> */}
                  {isSectionCollapsed('settings') ? (
                    <i className="pull-right bold fa fa-angle-right" />
                  ) : (
                    <i className="pull-right bold fa fa-angle-down" />
                  )}
                </a>
                {!isSectionCollapsed('settings') && (
                  <ul className="side-nav-sublist">
                    <SideNavItemCustom to="/settings/my-edge-rcm" name="MY_EDGE_RCM_SETTINGS">
                      <span className="fs14">{t('MY_EDGE_RCM_SETTINGS', 'My Edge RCM Settings')}</span>
                    </SideNavItemCustom>
                    {hasAccess('settingsAccess') && (
                      <>
                        <SideNavItemCustom to="/settings/watermarks" name="WATERMARKS_TITLE">
                          <span className="fs14">{t('WATERMARKS_TITLE', 'Watermarks')}</span>
                        </SideNavItemCustom>

                        <SideNavItemCustom to="/settings/metadata" name="METADATA_TITLE">
                          <span className="fs14">{t('METADATA_TITLE', 'Metadata')}</span>
                        </SideNavItemCustom>

                        <SideNavItemCustom to="/settings/document-types" name="TYPES_TITLE">
                          <span className="fs14">{t('TYPES_TITLE', 'Types')}</span>
                        </SideNavItemCustom>

                        <SideNavItemCustom to="/settings/folder-types" name="FOLDER_TYPES_TITLE">
                          <span className="fs14">{t('FOLDER_TYPES_TITLE', 'Folder Types')}</span>
                        </SideNavItemCustom>

                        <SideNavItemCustom to="/settings/workflow-templates" name="WORKFLOW_TEMPLATES_TITLE">
                          <span className="fs14">{t('CHANGE_REQUEST_TYPES_TITLE', 'Change Request Types')}</span>
                        </SideNavItemCustom>

                        <SideNavItemCustom
                          to="/settings/folder-structure-template-types"
                          name="FOLDER_STRUCTURE_TEMPLATE_TYPES_TITLE"
                        >
                          <span className="fs14">
                            {t('FOLDER_STRUCTURE_TEMPLATE_TYPES_TITLE', 'Folder Structure Template Types')}
                          </span>
                        </SideNavItemCustom>

                        <SideNavItemCustom to="/settings/tags" name="TAGS_TITLE">
                          <span className="fs14">{t('TAGS_TITLE', 'Tags')}</span>
                        </SideNavItemCustom>
                      </>
                    )}
                  </ul>
                )}
              </li>
            )}

            {/* System Section */}
            {hasAccess('systemAccess') && (
              <li className="side-nav-item">
                <a
                  ref={sectionRefs.current['system']}
                  onClick={() => toggleSectionCollapse('system')}
                  className={
                    sideNavContext.activeMenuSection === sectionRefs.current['system'] ||
                    (sectionRefs.current['system']?.current &&
                      sideNavContext.activeMenuSection?.current &&
                      sectionRefs.current['system']?.current.contains(sideNavContext.activeMenuSection.current))
                      ? ' active'
                      : ''
                  }
                  title={sideNavContext.isMenuCollapsed ? t('RIGHT_TOP_NAV_SYSTEM', 'System') : ''}
                  data-test-id="main-nav-system"
                >
                  {t('RIGHT_TOP_NAV_SYSTEM', 'System')}
                  {/* <span className="side-nav-arrow">{isSectionCollapsed('system') ? '▼' : '▲'}</span> */}
                  {isSectionCollapsed('system') ? (
                    <i className="pull-right bold fa fa-angle-right" />
                  ) : (
                    <i className="pull-right bold fa fa-angle-down" />
                  )}
                </a>
                {!isSectionCollapsed('system') && (
                  <ul className="side-nav-sublist">
                    {hasAccess('systemAccessSystemPerformance') && (
                      <SideNavItemCustom to="/system/system-performance" name="SYSTEM_SYSTEM_INFORMATION_TITLE">
                        <span className="fs14">{t('SYSTEM_SYSTEM_INFORMATION_TITLE', 'System Information')}</span>
                      </SideNavItemCustom>
                    )}
                    {hasAccess('systemAccessPolicies') && (
                      <SideNavItemCustom to="/system/policies" name="SETTINGS_POLICIES_TITLE">
                        <span className="fs14">{t('SETTINGS_POLICIES_TITLE', 'Policies')}</span>
                      </SideNavItemCustom>
                    )}
                    {(safeFeatures.etmfFeature?.show || safeFeatures.rcrFeature?.show) &&
                      hasAccess('systemAccessEtmfAccess') && (
                        <SideNavItemCustom to="/system/EtmfSetup" name="SETTINGS_ETMF_SETUP_TITLE">
                          <span className="fs14">{t('SETTINGS_ETMF_SETUP_TITLE', 'Clinical Setup')}</span>
                        </SideNavItemCustom>
                      )}
                    {hasAccess('systemAccessTextAndMessages') && (
                      <SideNavItemCustom to="/system/strings" name="SETTINGS_TEXT_AND_MESSAGES_TITLE">
                        <span className="fs14">{t('SETTINGS_TEXT_AND_MESSAGES_TITLE', 'Text and Messages')}</span>
                      </SideNavItemCustom>
                    )}
                    {!safeFeatures.eosFeature?.show && hasAccess('systemAccessIntegrations') && (
                      <SideNavItemCustom to="/system/integrations" name="SYSTEM_INTEGRATIONS_TITLE">
                        <span className="fs14">{t('SYSTEM_INTEGRATIONS_TITLE', 'Integrations')}</span>
                      </SideNavItemCustom>
                    )}
                    {hasAccess('systemAccessUsers') && (
                      <SideNavItemCustom to="/system/users" name="USERS_TITLE">
                        <span className="fs14">{t('USERS_TITLE', 'Users')}</span>
                      </SideNavItemCustom>
                    )}
                    {hasAccess('systemAccessGroups') && (
                      <SideNavItemCustom to="/system/groups" name="GROUPS_TITLE">
                        <span className="fs14">{t('GROUPS_TITLE', 'Groups')}</span>
                      </SideNavItemCustom>
                    )}
                  </ul>
                )}
              </li>
            )}
          </ul>
        </div>
      </Sidebar>
    </>
  )
}
